import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Default} from "../util/decorators";
import {ObjectCostRateModelArrayValidator, ObjectCostRateModelEntity} from "./object-cost-rate-model";
import {TicketValidationStatus, toTicketValidationStatus} from "./ticket-validation-status";
import {TransportEntity, TransportPayload, TransportValidator} from "./transport";
import {TransportCategorizable, transportCategory, TransportCategory} from "./transport-category";
import {getDatabaseCodeTransformer, getTransportCategoryTransformer} from "./transfomers/transformers";
import {Warning, WarningArrayValidator} from "./warning";
import {CostedEntity} from "./costed-entity";
import {TicketCodeArrayValidator, TicketCodeEntity} from "./ticket-code";
import {DispatchArrayValidator, DispatchEntity} from "./dispatch";
import {MessageEntity} from "./message";
import {
  RrcEp10WasteManifestBuildStatusCodec,
  RrcEp10WasteManifestBuildStatusEntity
} from "./rrc/rrc-ep10-waste-manifest-build-status";

/** Validator for transport assoc types. */
export const TransportAssocValidator = t.intersection([
  t.type({
    id: t.string,
    category: t.string
  }),
  t.partial({
    regionId: t.string,
    pickupTransportId: t.string,
    deliveryTransportId: t.string,
    pickupTransport: TransportValidator,
    deliveryTransport: TransportValidator,
    costedShiftId: t.string,
    holderAvailable: t.boolean,
    pickupHolderAvailable: t.boolean,
    deliveryHolderAvailable: t.boolean,
    shiftHolderAvailable: t.boolean,
    pickupDttm: t.number,
    deliveryDttm: t.number,
    currentStartShiftDttm: t.number,
    startShiftDttm: t.number,
    endShiftDttm: t.number,
    prevTransportDttm: t.number,
    prevTransportOutDttm: t.number,
    pickupQty: t.number,
    deliveryQty: t.number,
    fuelPrice: t.number,
    mileage: t.number,
    loadedMileage: t.number,
    unloadedMileage: t.number,
    manualLoadedMileage: t.number,
    manualUnloadedMileage: t.number,
    odometerLoadedMileage: t.number,
    odometerUnloadedMileage: t.number,
    shiftOdometerLoadedMileage: t.number,
    shiftOdometerUnloadedMileage: t.number,
    gpsLoadedMileage: t.number,
    gpsUnloadedMileage: t.number,
    shiftGpsLoadedMileage: t.number,
    shiftGpsUnloadedMileage: t.number,
    projectedMileage: t.number,
    projectedLoadedMileage: t.number,
    projectedUnloadedMileage: t.number,
    projectedOdometerLoadedMileage: t.number,
    projectedOdometerUnloadedMileage: t.number,
    projectedShiftOdometerLoadedMileage: t.number,
    projectedShiftOdometerUnloadedMileage: t.number,
    projectedGpsLoadedMileage: t.number,
    projectedGpsUnloadedMileage: t.number,
    projectedShiftGpsLoadedMileage: t.number,
    projectedShiftGpsUnloadedMileage: t.number,
    objectCostRateModelList: ObjectCostRateModelArrayValidator,
    ticketCodeList: TicketCodeArrayValidator,
    warnings: WarningArrayValidator,
    preTta: t.string,
    postTta: t.string,
    pickupMultipartRecord: t.boolean,
    /** The companies on the ticket and the part each plays: operator, provider, disposal company, marketer… */
    transportParties: t.array(t.intersection([
      t.type({companyId: t.string, roleKey: t.string}),
      t.partial({id: t.string, companyName: t.string}),
    ])),
    deliveryMultipartRecord: t.boolean,
    disputed: t.boolean,
    hasBeenDisputed: t.boolean,
    adminValidatedById: t.string,
    adminValidatedDttm: t.number,
    adminValidatorName: t.string,
    dispatchValidatedById: t.string,
    dispatchValidatedDttm: t.number,
    dispatchValidatorName: t.string,
    fieldValidatedById: t.string,
    fieldValidatedDttm: t.number,
    fieldValidatorName: t.string,
    businessValidatedById: t.string,
    businessValidatedDttm: t.number,
    businessValidatorName: t.string,
    /** Set once the quantity has been validated and locked. */
    qtyValidatedById: t.string,
    qtyValidatedDttm: t.number,
    holdingRecord: t.boolean,
    tabletHolding: t.boolean,
    validationStatus: t.string,
    billableTime: t.number,
    turnAroundTime: t.number,
    dispatchRequestId: t.string,
    dispatchRequestResourceIdText: t.string,
    requesterName: t.string,
    dispatchId: t.string,
    dispatcherName: t.string,
    dispatches: DispatchArrayValidator,
    resourceIdentifiers: t.readonlyArray(t.string),
    rrcEp10WasteManifestBuildStatus: RrcEp10WasteManifestBuildStatusCodec,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    database: t.string
  })
]);

/** Validator for an array of transport assoc types. */
export const TransportAssocArrayValidator = t.array(TransportAssocValidator);

export type TransportAssoc = t.TypeOf<typeof TransportAssocValidator>;
export type TransportAssocArray = t.TypeOf<typeof TransportAssocArrayValidator>;

/**
 * A transport - transport association.
 */
@Exclude()
export class TransportAssocEntity extends CostedEntity implements TransportCategorizable {
  @Expose()
  @Default("-1")
  id!: string;

  @Expose() pickupTransportId?: string;
  @Expose() deliveryTransportId?: string;
  @Expose()
  @Transform(getTransportCategoryTransformer())
  category: TransportCategory = transportCategory.water;
  @Expose() @Type(() => TransportEntity) pickupTransport?: TransportEntity;
  @Expose() @Type(() => TransportEntity) deliveryTransport?: TransportEntity;
  @Expose() costedShiftId?: string;
  @Expose() holderAvailable?: boolean;
  @Expose() pickupHolderAvailable?: boolean;
  @Expose() deliveryHolderAvailable?: boolean;
  @Expose() shiftHolderAvailable?: boolean;
  @Expose() pickupDttm?: number;
  @Expose() deliveryDttm?: number;
  @Expose() currentStartShiftDttm?: number;
  @Expose() startShiftDttm?: number;
  @Expose() endShiftDttm?: number;
  @Expose() prevTransportDttm?: number;
  @Expose() prevTransportOutDttm?: number;

  @Expose()
  @Default([])
  resourceIdentifiers: ReadonlyArray<string> = [];

  @Expose() turnAroundTime?: number;
  @Expose() billableTime?: number;
  @Expose() pickupQty?: number;
  @Expose() deliveryQty?: number;

  @Expose()
  fuelPrice?: number

  @Expose()
  mileage?: number


  @Expose()
  loadedMileage?: number;

  @Expose()
  unloadedMileage?: number;

  @Expose()
  manualLoadedMileage?: number;

  @Expose()
  manualUnloadedMileage?: number;

  @Expose()
  odometerLoadedMileage?: number;

  @Expose()
  odometerUnloadedMileage?: number;

  @Expose()
  shiftOdometerLoadedMileage?: number;

  @Expose()
  shiftOdometerUnloadedMileage?: number;

  @Expose()
  gpsLoadedMileage?: number;

  @Expose()
  gpsUnloadedMileage?: number;

  @Expose()
  shiftGpsLoadedMileage?: number;

  @Expose()
  shiftGpsUnloadedMileage?: number;

  @Expose()
  projectedMileage?: number;

  @Expose()
  projectedLoadedMileage?: number;

  @Expose()
  projectedUnloadedMileage?: number;

  @Expose()
  projectedOdometerLoadedMileage?: number;

  @Expose()
  projectedOdometerUnloadedMileage?: number;

  @Expose()
  projectedShiftOdometerLoadedMileage?: number;

  @Expose()
  projectedShiftOdometerUnloadedMileage?: number;

  @Expose()
  projectedGpsLoadedMileage?: number;

  @Expose()
  projectedGpsUnloadedMileage?: number;

  @Expose()
  projectedShiftGpsLoadedMileage?: number;

  @Expose()
  projectedShiftGpsUnloadedMileage?: number;

  @Expose({name: "objectCostRateModelList"})
  @Type(() => ObjectCostRateModelEntity)
  objectCostRateModels: ObjectCostRateModelEntity[] = [];

  @Expose({name: "ticketCodeList"})
  @Type(() => TicketCodeEntity)
  ticketCodes: TicketCodeEntity[] = [];

  @Expose({name: "dispatches"})
  @Type(() => DispatchEntity)
  dispatches?: DispatchEntity[]

  @Expose()
  warnings?: Warning[]

  @Expose() createDttm?: number;
  @Expose() lastModifiedDttm?: number;
  @Expose() readonly database?: string;

  // Split Load Fields
  @Expose() preTtaId?: string;
  @Expose() postTtaId?: string;
  @Expose() pickupMultipartRecord = false;
  @Expose() deliveryMultipartRecord = false;

  // validation fields
  @Expose() adminValidatedDttm?: number;
  @Expose() adminValidatedById?: string;
  @Expose() adminValidatorName?: string;
  @Expose() dispatchValidatedDttm?: number;
  @Expose() dispatchValidatedById?: string;
  @Expose() dispatchValidatorName?: string;
  @Expose() fieldValidatedDttm?: number;
  @Expose() fieldValidatedById?: string;
  @Expose() fieldValidatorName?: string;
  @Expose() businessValidatedDttm?: number;
  @Expose() businessValidatedById?: string;
  @Expose() businessValidatorName?: string;
  @Expose() qtyValidatedById?: string;
  @Expose() qtyValidatedDttm?: number;
  @Expose() readonly holdingRecord?: boolean;
  @Expose() readonly tabletHolding?: boolean;
  @Expose()
  @Transform(getDatabaseCodeTransformer(toTicketValidationStatus))
  validationStatus?: TicketValidationStatus;

  // dispatch fields
  @Expose() dispatchRequestId?: string;
  @Expose() dispatchRequestResourceIdText?: string;
  @Expose() requesterName?: string;
  @Expose() dispatchId?: string;
  @Expose() dispatcherName?: string;

  // rrc fields
  @Expose()
  @Type(() => RrcEp10WasteManifestBuildStatusEntity)
  rrcEp10WasteManifestBuildStatus?: RrcEp10WasteManifestBuildStatusEntity

  @Expose() regionId?: string

  readonly entityType = "TRANSPORTTRANSPORTASSOC";

  get displayName(): Readonly<string> {
    return `Transport - Transport Assoc : ${this.id}`;
  }

  /**
   * Constructor for TransportAssocEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
    super()
  }

  /**
   * Create a TransportAssocEntity from a Transport type value.
   *
   * @param transportAssoc The transport assoc type value.
   */
  static from(transportAssoc: TransportAssoc): TransportAssocEntity {
    return plainToInstance(
      TransportAssocEntity,
      transportAssoc,
      {
        excludeExtraneousValues: true
      }
    );
  }

  static loadTypeValues(entities: TransportAssocEntity[]): Promise<TransportAssoc[]> {
    const objects = instanceToPlain(entities, {
      excludeExtraneousValues: true
    });
    return validateJSON(TransportAssocArrayValidator, objects);
  }

  /**
   * Deserializes the TransportAssocEntity to a TransportAssoc type value.
   * If validation fails, the promise will be rejected.
   */
  loadTypeValue(): Promise<TransportAssoc> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true
    });
    return validateJSON(TransportAssocValidator, object);
  }

  asTypeValue(): TransportAssoc {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(TransportAssocValidator, object);
  }

  get serviceTypeName(): string | undefined {
    if (this.pickupTransport?.serviceTypeId) {
      return (
        this.pickupTransport.serviceTypeAbbr ??
        this.pickupTransport.serviceTypeName ??
        "Unknown"
      );
    } else if (this.deliveryTransport?.serviceTypeId) {
      return (
        this.deliveryTransport.serviceTypeAbbr ??
        this.deliveryTransport.serviceTypeName ??
        "Unknown"
      );
    }
    return undefined;
  }

  get startDttm(): number | undefined {
    let dttm = this.startShiftDttm;
    if (!dttm) {
      dttm = this.prevTransportOutDttm;
    }
    if (!dttm) {
      dttm = this.prevTransportDttm;
    }
    if (
      !dttm &&
      this.pickupTransport &&
      this.pickupTransport.createDttm &&
      this.pickupTransport.inDttm
    ) {
      dttm =
        this.pickupTransport.inDttm < this.pickupTransport.createDttm
          ? this.pickupTransport.inDttm
          : this.pickupTransport.createDttm;
    }
    return dttm;
  }

  get endDttm(): number | undefined {
    let dttm = this.endShiftDttm;
    if (
      !dttm &&
      this.deliveryTransport?.outDttm &&
      this.deliveryTransport.createDttm
    ) {
      dttm =
        this.deliveryTransport.outDttm > this.deliveryTransport.createDttm
          ? this.deliveryTransport.outDttm
          : this.deliveryTransport.createDttm;
    }
    return dttm;
  }

  get messages() : MessageEntity[] {
    const messages : MessageEntity[] = []

    let transport = this.pickupTransport
    if (transport) {
      if (transport.messages) {
        messages.push(...transport.messages)
      }
    }
    transport = this.deliveryTransport
    if (transport) {
      if (transport.messages) {
        messages.push(...transport.messages)
      }
    }

    return messages
  }
}

export interface MapTtasCriteria {
  mappingFileId: string,
  companyId?: string
  file: File
}

export interface TtaSave {
  id?: string
  pickupTransportId?: string
  deliveryTransportId?: string
  regionId?: string
  category?: string
  manualLoadedMileage?: number
  manualUnloadedMileage?: number
  pickupTransport: TransportPayload,
  deliveryTransport: TransportPayload,
}

export const buildTtaSave = (tta: TransportAssocEntity): TtaSave => {
  const pickup : TransportPayload = {
    ...(tta.pickupTransport?.asTypeValue() ?? {})
  }

  const delivery : TransportPayload = {
    ...(tta.deliveryTransport?.asTypeValue() ?? {})
  }
  return {
    id: tta.id,
    pickupTransportId: tta.pickupTransportId,
    deliveryTransportId: tta.deliveryTransportId,
    pickupTransport: pickup,
    deliveryTransport: delivery,
    regionId: tta.regionId ?? pickup.regionId ?? delivery.regionId,
    category: tta.category?.code ?? pickup.category ?? delivery.category,
    manualLoadedMileage: tta.manualLoadedMileage,
    manualUnloadedMileage: tta.manualUnloadedMileage,
  }
}

/** Where a save put a record: its live table, the holding table, or nowhere. */
export type TicketRecordLocation = "LIVE" | "HOLDING" | "NONE"

export interface TicketSaveReceipt {
  resourceId?: number
  /** The association that was written, live or holder. */
  ttaId?: string
  operation?: "CREATED" | "UPDATED"
  location?: TicketRecordLocation
  ttaLocation?: TicketRecordLocation
  pickupLocation?: TicketRecordLocation
  deliveryLocation?: TicketRecordLocation
  holding: boolean
}

export interface TicketValidationDTO {
  adminValidated?: boolean
  dispatchValidated?: boolean
  fieldValidated?: boolean
  businessValidated?: boolean
}

export interface MultiTicketValidationDTO {
  ticketId: string
  serviceLookup?: string;
  validationDTO: TicketValidationDTO
}
